import React, { useEffect, useState } from 'react'
import { Button, makeStyles, Paper, Typography, Box } from '@material-ui/core'
import Tooltip from '@material-ui/core/Tooltip'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import ResourceNotFound from 'components/ResourceNotFound'
import { EditOutlined as EditIcon, DeleteOutlineOutlined as DeleteIcon } from '@material-ui/icons'
import ConfirmModal from 'components/ConfirmationModal'
import getTableOptions from 'utils/getTableOptions'
import buildingSlice, {
  fetchBuildings,
  deleteBuildingById,
  checkBuildingExistsInTalks,
  checkBuildingExistsInEvents
} from 'store/building'
import { toast } from 'utils/notify'
import BasicTable from 'components/BasicTable'
import { renderCol } from 'utils/renderColumns'
import ImportBuildingCSVModal from 'views/admin/building/ImportBuildingCSVModal'
import { KeyboardArrowRight } from '@mui/icons-material'

const BuildingListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { user } = useSelector((state) => state.auth)
  const { buildings, currentPage, pageSize, inProgress } = useSelector((state) => state.building)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [buildingToDelete, setBuildingToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    const unsubBuildings = fetchBuildings(user?.conferenceId, (buildingsData) => {
      dispatch(buildingSlice.actions.setBuildings(buildingsData))
    })

    return () => unsubBuildings && unsubBuildings()
  }, [dispatch, user?.conferenceId])

  const columns = [
    {
      name: 'Title',
      label: 'Title',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'Floor',
      label: 'Floor',
      options: {
        filter: true,
        sort: false
      }
    },
    {
      name: 'Room #',
      label: 'Room #',
      options: {
        filter: true,
        sort: false
      }
    },
    {
      name: 'Actions',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]
  const formatTableData = (buildings) =>
    buildings.map((building) => [
      building.title,
      building.floorNum,
      building.roomNum,
      <>
        <Tooltip title="Edit">
          <EditIcon
            color="action"
            style={{ cursor: 'pointer' }}
            onClick={() => history.push(`${location.pathname}/edit/${building.id}`)}
          />
        </Tooltip>
        <Tooltip title="Delete">
          <DeleteIcon
            color="action"
            style={{ marginLeft: 5, cursor: 'pointer' }}
            onClick={() => setBuildingToDelete(building)}
          />
        </Tooltip>
      </>
    ])

  const handleDelete = async () => {
    if (!buildingToDelete) return
    setDeleting(true)
    try {
      const isBuildingExistInTalk = await dispatch(checkBuildingExistsInTalks(buildingToDelete.id))
      const isBuildingExistInEvents = await dispatch(
        checkBuildingExistsInEvents(buildingToDelete.id)
      )
      if (isBuildingExistInTalk || isBuildingExistInEvents) {
        toast.error(`Remove this building from all Events and Talks before deleting it.`)
      } else {
        await dispatch(deleteBuildingById(user?.conferenceId, buildingToDelete.id))
        toast.success('Building deleted.')
      }
    } finally {
      setDeleting(false)
      setBuildingToDelete(null)
    }
  }

  const options = getTableOptions()

  return (
    <>
      <Box className={classes.pageHeader}>
        <Typography variant="h5" className={classes.pageTitle}>
          Buildings
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            endIcon={<KeyboardArrowRight />}
            onClick={() => setImportModalOpen(true)}>
            Import via CSV
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            endIcon={<KeyboardArrowRight />}
            onClick={() => history.push(`${location.pathname}/new`)}>
            Create
          </Button>
        </div>
      </Box>

      <ConfirmModal
        dialogTitle="Delete Building"
        dialogText="Are you sure you want to delete this building?"
        open={!!buildingToDelete}
        closeModal={() => setBuildingToDelete(null)}
        handleConfirm={handleDelete}
        inProgress={deleting}
      />

      <ImportBuildingCSVModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        conferenceId={user?.conferenceId}
        buildings={buildings}
      />

      {buildings && buildings.length ? (
        <Paper variant="outlined" style={{ marginTop: 20 }}>
          <BasicTable
            data={formatTableData(buildings)}
            columns={columns}
            options={{
              ...options,
              onChangePage: (page) => dispatch(buildingSlice.actions.setCurrentPage(page)),
              onChangeRowsPerPage: (size) => dispatch(buildingSlice.actions.setPageSize(size)),
              page: currentPage,
              rowsPerPage: pageSize
            }}
          />
        </Paper>
      ) : (
        !inProgress && <ResourceNotFound message="No building available" />
      )}
    </>
  )
}

export default BuildingListing

const useStyles = makeStyles((theme) => ({
  pageTitle: {
    fontWeight: 600,
    paddingBottom: theme.spacing(1),
    fontSize: '26px'
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }
}))
